import { useMemo, useState } from 'react';
import axios from 'axios';
import { BadgeDollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_API_URL;

const COPY = {
  tr: {
    open: 'Eksik Fiyatları Doldur',
    title: 'Eksik Fiyatları Toplu Doldur',
    intro: 'Fiyatı olmayan aktif katalog kalemlerini tek ekranda tamamlayın.',
    loading: 'Eksik fiyatlar yükleniyor…',
    empty: 'Fiyatı eksik aktif katalog kalemi bulunmuyor.',
    sku: 'SKU',
    unit: 'Birim',
    price: 'Birim fiyat',
    close: 'Kapat',
    save: 'Doldurulan Fiyatları Kaydet',
    saving: 'Kaydediliyor…',
    noneFilled: 'Kaydetmek için en az bir geçerli fiyat girin.',
    invalid: 'Fiyatlar 0’dan büyük olmalıdır.',
    success: 'fiyat güncellendi.',
    failed: 'Toplu fiyat güncellemesi tamamlanamadı.',
  },
  en: {
    open: 'Fill Missing Prices',
    title: 'Fill Missing Prices',
    intro: 'Complete active catalog items without a price in one screen.',
    loading: 'Loading missing prices…',
    empty: 'There are no active catalog items with a missing price.',
    sku: 'SKU',
    unit: 'Unit',
    price: 'Unit price',
    close: 'Close',
    save: 'Save Entered Prices',
    saving: 'Saving…',
    noneFilled: 'Enter at least one valid price to save.',
    invalid: 'Prices must be greater than zero.',
    success: 'prices updated.',
    failed: 'Bulk price update could not be completed.',
  },
};

const errorMessage = (error, fallback) => (
  typeof error.response?.data?.detail === 'string' ? error.response.data.detail : fallback
);

export function CatalogBulkPriceDialog({ lang = 'tr', missingCount = 0, onSaved }) {
  const text = COPY[lang === 'en' ? 'en' : 'tr'];
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const filledCount = useMemo(() => Object.values(drafts).filter((value) => {
    const parsed = Number(String(value).trim().replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0;
  }).length, [drafts]);

  const loadMissing = async () => {
    setLoading(true);
    setDrafts({});
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/catalog`, {
        params: { is_active: true, page_size: 200 },
      });
      setItems((data.items || []).filter((item) => Number(item.unit_price_minor || 0) <= 0));
    } catch (error) {
      toast.error(errorMessage(error, text.failed));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) loadMissing();
  };

  const save = async () => {
    const payload = items.flatMap((item) => {
      const raw = String(drafts[item.id] ?? '').trim();
      if (!raw) return [];
      const normalized = raw.replace(',', '.');
      if (!/^\d+(\.\d{1,4})?$/.test(normalized) || Number(normalized) <= 0) {
        return [{ invalid: true, id: item.id }];
      }
      return [{ id: item.id, unit_price_major: raw }];
    });
    if (payload.some((row) => row.invalid)) {
      toast.error(text.invalid);
      return;
    }
    if (!payload.length) {
      toast.error(text.noneFilled);
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/quote/catalog/prices/bulk`, { items: payload });
      toast.success(`${data.updated} ${text.success}`);
      setOpen(false);
      await onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, text.failed));
    } finally {
      setSaving(false);
    }
  };

  return <>
    <Button type="button" variant="outline" onClick={() => onOpenChange(true)} data-testid="catalog-bulk-price-open">
      <BadgeDollarSign className="mr-2 h-4 w-4" />
      <span>{text.open}</span>
      {missingCount > 0 && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{missingCount}</span>}
    </Button>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] max-w-3xl overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:max-h-[90vh]" data-testid="catalog-bulk-price-dialog">
        <DialogHeader>
          <DialogTitle>{text.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{text.intro}</p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-8 text-center text-sm text-emerald-800">{text.empty}</div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} className="grid gap-3 rounded-xl border border-slate-200 p-3 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">{item.name}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>{text.sku}: {item.sku || '—'}</span>
                    <span>{text.unit}: {item.unit || '—'}</span>
                  </div>
                </div>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={drafts[item.id] || ''}
                  onChange={(event) => setDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                  placeholder="0,00"
                  aria-label={`${item.name} ${text.price}`}
                  data-testid={`bulk-price-${item.id}`}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>{text.close}</Button>
          {items.length > 0 && (
            <Button type="button" onClick={save} disabled={saving || filledCount === 0}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? text.saving : `${text.save} (${filledCount})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
